import React, { useState, useEffect } from "react";
import { QueryList, QueryToolCell } from "@veiligh/reporting-core/ui";
import { toast } from "sonner";

import ClientTableForData from "~/components/shared/client-paginated-table/table";
import { Button, ConfirmDeleteDialog, Icon } from "~/components/ui";
import { useReportingContext } from "@veiligh/reporting-core/ui";
import { useNavigate } from "react-router-dom";

const ReportsNewQueryListPage: React.FC = () => {
  const {
    queries,
    isLoading,
    reloadQueries,
    isDeleting,
    setIsDeleting,
    dataAdapter,
  } = useReportingContext();
  const navigate = useNavigate();
  const [searchInput] = useState("");
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [selectedItemId, setSelectedItemId] = useState("");

  // Always load queries including cloned on mount
  useEffect(() => {
    reloadQueries({ includeCloned: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredQueries = React.useMemo(() => {
    let result = queries;
    if (searchInput) {
      const lowerSearch = searchInput.toLowerCase();
      result = result.filter((q) => q.name.toLowerCase().includes(lowerSearch));
    }
    return result;
  }, [queries, searchInput]);

  const handleDelete = async (id: string) => {
    try {
      setSelectedItemId(id);
      setIsDeleting(true);
      await dataAdapter.deleteQuery(id);
      toast.success("Query deleted successfully");
      await reloadQueries({ includeCloned: true });
    } catch (error) {
      toast.error("Failed to delete query");
    } finally {
      setIsDeleting(false);
      setSelectedItemId("");
    }
  };

  const handleBulkDelete = async () => {
    const idsToDelete = Object.keys(rowSelection).filter(
      (k) => rowSelection[k],
    );
    if (idsToDelete.length === 0) return;

    try {
      setIsDeleting(true);
      await Promise.all(idsToDelete.map((id) => dataAdapter.deleteQuery(id)));
      toast.success("Queries deleted successfully");
      setRowSelection({});
      await reloadQueries({ includeCloned: true });
    } catch (error) {
      toast.error("Failed to delete queries");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRefresh = () => {
    reloadQueries({ includeCloned: true });
  };

  return (
    <QueryList
      data={filteredQueries}
      isLoading={isLoading}
      isFetching={false}
      searchInput={searchInput}
      onRefresh={handleRefresh}
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
      onBulkDelete={handleBulkDelete}
      isDeleting={isDeleting}
      selectedItemId={selectedItemId}
      onDelete={handleDelete}
      canDelete={true}
      createQueryPath="/hse/reports/query/create"
      buildEditQueryPath={(id: string) => `/hse/reports/query/edit/${id}`}
      onNavigate={navigate}
      TableComponent={ClientTableForData}
      ButtonComponent={Button}
      IconComponent={Icon}
      ConfirmDeleteDialogComponent={ConfirmDeleteDialog}
      TableCheckboxColumn={() => null} // handled by client-table natively
      ToolCellComponent={QueryToolCell as unknown as React.ComponentType<any>}
    />
  );
};

export default ReportsNewQueryListPage;
